import type { CorpsOffre, OffrePresentee, CorpsGesteOffre } from './offres.js';
import type { CorpsReservation, ReservationPresentee } from './reservations.js';
import type { CorpsFacture, FacturePresentee } from './ventes.js';
import {
  DEMO_CONTACTS,
  DEMO_OEUVRES,
  DEMO_OFFRES,
  DEMO_RESERVATIONS,
  DEMO_FACTURES,
} from './demoFixtures.js';
import { ApiError } from './errors.js';

export const offresDemo: OffrePresentee[] = structuredClone(DEMO_OFFRES);
export const reservationsOffresDemo: ReservationPresentee[] = [];
export const facturesOffresDemo: FacturePresentee[] = [];
function introuvable(): never {
  throw new ApiError(404, 'ressource.introuvable', 'Introuvable.');
}
function lignesDemo(corps: CorpsOffre) {
  return corps.lignes.map((l) => {
    const o = DEMO_OEUVRES.find((o) => o.id === l.oeuvreId) ?? introuvable();
    return {
      oeuvreId: o.id,
      exemplaireId: l.exemplaireId ?? null,
      designation: o.titre,
      cartel: {
        titre: o.titre,
        artiste: o.artisteNom,
        ...(o.annee ? { annee: String(o.annee) } : {}),
        ...(o.technique ? { technique: o.technique.fr } : {}),
        numeroInventaire: o.numeroInventaire,
      },
      prixPublicCentimes: o.prixPublicCentimes,
      prixProposeCentimes: l.prixProposeCentimes,
    };
  });
}
/** Gestes simulés uniquement : aucune requête réelle, réservation ou facture extérieure. */
export function mutationOffresDemo(
  chemin: string,
  methode: string,
  corps?: unknown,
): unknown | undefined {
  const maintenant = new Date().toISOString();
  if (chemin === '/offres' && methode === 'POST') {
    const entree = corps as CorpsOffre;
    const contact = DEMO_CONTACTS.find((c) => c.id === entree.contactId) ?? introuvable();
    const lignes = lignesDemo(entree);
    const offre: OffrePresentee = {
      ...structuredClone(DEMO_OFFRES[0]!),
      id: `offre-demo-${crypto.randomUUID()}`,
      numero: `O-${new Date().getFullYear()}-${String(offresDemo.length + 1).padStart(4, '0')}`,
      contactId: contact.id,
      contactNom: contact.nomAffiche,
      lignes,
      totalCentimes: lignes.reduce((s, l) => s + l.prixProposeCentimes, 0),
      validiteJusquau: entree.validiteJusquau,
      langue: entree.langue ?? contact.langue,
      message: entree.message ?? '',
      expositionId: entree.expositionId ?? null,
      canal: entree.canal ?? null,
      etat: entree.canal === 'stand' ? 'envoyee' : 'brouillon',
      envoyeeLe: entree.canal === 'stand' ? maintenant : null,
      envois: [],
      reservationIds: [],
      factureId: null,
      creeeLe: maintenant,
      modifieeLe: maintenant,
    };
    offresDemo.unshift(offre);
    return { offre };
  }
  if (chemin === '/reservations' && methode === 'POST') {
    const entree = corps as CorpsReservation;
    const reservation: ReservationPresentee = {
      ...entree,
      id: `reservation-demo-${crypto.randomUUID()}`,
      exemplaireId: entree.exemplaireId ?? null,
      note: entree.note ?? '',
      etat: 'active',
      leveeLe: null,
      creeeLe: maintenant,
      modifieeLe: maintenant,
    };
    reservationsOffresDemo.push(reservation);
    return { reservation };
  }
  if (chemin === '/ventes/factures' && methode === 'POST') {
    const entree = corps as CorpsFacture;
    const facture: FacturePresentee = {
      ...structuredClone(DEMO_FACTURES[0]!),
      id: `facture-demo-${crypto.randomUUID()}`,
      contactId: entree.contactId,
      contactNom: DEMO_CONTACTS.find((c) => c.id === entree.contactId)?.nomAffiche ?? '',
      numero: null,
      etat: 'brouillon',
      emiseLe: null,
      devise: entree.devise ?? 'EUR',
      langue: entree.langue ?? 'fr',
      lignes: entree.lignes.map((l) => ({
        ...DEMO_FACTURES[0]!.lignes[0]!,
        oeuvreId: l.oeuvreId ?? null,
        exemplaireId: l.exemplaireId ?? null,
        designation: DEMO_OEUVRES.find((o) => o.id === l.oeuvreId)?.titre ?? '',
        quantite: 1,
        prixUnitaireCentimes: l.prixUnitaireCentimes ?? 0,
        prixUnitaireNetCentimes: l.prixUnitaireCentimes ?? 0,
        cartel: { titre: DEMO_OEUVRES.find((o) => o.id === l.oeuvreId)?.titre },
        remisePointsDeBase: 0,
      })),
      creeLe: maintenant,
      modifieLe: maintenant,
    };
    const total = entree.lignes.reduce((s, l) => s + (l.prixUnitaireCentimes ?? 0), 0);
    const calculee = {
      ...facture,
      totaux: {
        totalHtCentimes: total,
        totalTtcCentimes: total,
        totalTvaCentimes: 0,
        droitDeSuiteCentimes: 0,
        parCategorieTva: [],
      },
      totauxFiges: false,
      montantRegleCentimes: 0,
      resteADevoirCentimes: total,
      pdfDisponible: false,
      facturXDisponible: false,
    };
    facturesOffresDemo.push(calculee);
    return { facture: calculee };
  }
  const match =
    /^\/offres\/([^/]+)(?:\/(envoyer|relancer|remettre|accepter|refuser|retirer|relance|liens))?$/.exec(
      chemin,
    );
  if (!match || methode === 'GET') return undefined;
  const offre = offresDemo.find((o) => o.id === match[1]) ?? introuvable();
  const nom = match[2];
  if (methode === 'DELETE' && !nom) {
    if (offre.etat !== 'brouillon')
      throw new ApiError(409, 'offre.verrouillee', 'Seul un brouillon peut être supprimé.');
    offresDemo.splice(offresDemo.indexOf(offre), 1);
    return null;
  }
  if (methode === 'PATCH' && !nom) {
    if (offre.etat !== 'brouillon')
      throw new ApiError(409, 'offre.verrouillee', 'L’offre est figée.');
    const entree = corps as Partial<CorpsOffre>;
    if (entree.contactId) {
      const c = DEMO_CONTACTS.find((c) => c.id === entree.contactId) ?? introuvable();
      offre.contactId = c.id;
      offre.contactNom = c.nomAffiche;
    }
    if (entree.lignes)
      offre.lignes = lignesDemo({ ...entree, lignes: entree.lignes } as CorpsOffre);
    if (entree.validiteJusquau) offre.validiteJusquau = entree.validiteJusquau;
    if (entree.message !== undefined) offre.message = entree.message;
    if (entree.langue) offre.langue = entree.langue;
    offre.totalCentimes = offre.lignes.reduce((s, l) => s + l.prixProposeCentimes, 0);
  } else if (nom === 'relance' && methode === 'PUT')
    offre.relancePrevueLe = (corps as { le: string | null }).le;
  else if (nom === 'liens' && methode === 'POST') {
    const entree = corps as { reservationId?: string; factureId?: string };
    if (
      entree.reservationId &&
      [...DEMO_RESERVATIONS, ...reservationsOffresDemo].some(
        (r) => r.id === entree.reservationId && r.contactId === offre.contactId,
      )
    )
      offre.reservationIds = [...new Set([...offre.reservationIds, entree.reservationId])];
    if (
      entree.factureId &&
      facturesOffresDemo.some((f) => f.id === entree.factureId && f.contactId === offre.contactId)
    )
      offre.factureId = entree.factureId;
  } else if (nom && methode === 'POST') {
    const entree = (corps ?? {}) as CorpsGesteOffre;
    const attendus: Record<string, string[]> = {
      envoyer: ['brouillon'],
      remettre: ['brouillon'],
      relancer: ['envoyee'],
      accepter: ['envoyee'],
      refuser: ['envoyee'],
      retirer: ['brouillon', 'envoyee'],
    };
    if (!attendus[nom]?.includes(offre.etat))
      throw new ApiError(409, 'offre.transition', 'Ce geste est indisponible dans cet état.');
    if (nom === 'envoyer' || nom === 'relancer') {
      offre.envois.push({
        type: nom === 'envoyer' ? 'envoi' : 'relance',
        le: maintenant,
        destinataire: entree.destinataire ?? '',
        resultat: 'capture',
      });
      // Une capture laisse l'offre en brouillon, comme l'API réelle.
      return { offre, envoi: { resultat: 'capture' } };
    }
    if (nom === 'remettre') {
      offre.etat = 'envoyee';
      offre.canal = 'remise';
      offre.envoyeeLe = maintenant;
    }
    if (nom === 'accepter') {
      offre.etat = 'acceptee';
      offre.accepteeLe = maintenant;
    }
    if (nom === 'refuser') {
      offre.etat = 'refusee';
      offre.refuseeLe = maintenant;
    }
    if (nom === 'retirer') {
      offre.etat = 'retiree';
      offre.retireeLe = maintenant;
    }
    offre.motif = entree.motif ?? '';
  } else throw new ApiError(405, 'methode.interdite', 'Geste indisponible en démonstration.');
  offre.modifieeLe = maintenant;
  return { offre };
}
